import React, { useState, useEffect } from 'react';
import { getTokenUsage, clearTokenUsage } from '../api';

// ============================================================================
// Token Dashboard Component
// ============================================================================

function TokenDashboard() {
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchTokenData = async () => {
    try {
      setLoading(true);
      const data = await getTokenUsage();
      setTokenData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTokenData();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchTokenData, 10000);
    return () => clearInterval(interval);
  }, []);
  
  const handleClear = async () => {
    if (window.confirm('Clear all token usage history?')) {
      try {
        await clearTokenUsage();
        await fetchTokenData();
      } catch (err) {
        setError(err.message);
      }
    }
  };
  
  if (loading && !tokenData) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading token usage data...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error">
        <p><strong>Error loading token data:</strong> {error}</p>
      </div>
    );
  }
  
  if (!tokenData || tokenData.total_queries === 0) {
    return (
      <div className="empty-state">
        <p>💡 No queries yet. Start asking questions to see token usage!</p>
      </div>
    );
  }
  
  return (
    <div className="token-dashboard">
      {/* Session Summary */}
      <section className="token-summary">
        <h3>Session Summary</h3>
        <div className="stat-cards">
          <div className="stat-card">
            <div className="stat-value">{tokenData.total_queries}</div>
            <div className="stat-label">Total Queries</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{tokenData.total_tokens.toLocaleString()}</div>
            <div className="stat-label">Total Tokens</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{tokenData.average_tokens_per_query.toFixed(0)}</div>
            <div className="stat-label">Avg Tokens/Query</div>
          </div>
        </div>
      </section>
      
      {/* Simple Bar Chart */}
      <section className="token-chart-section">
        <h3>Token Usage Over Time</h3>
        <TokenBarChart history={tokenData.history} />
      </section>
      
      {/* History Table */}
      <section className="token-history">
        <div className="section-header">
          <h3>Query History</h3>
          <button onClick={handleClear} className="btn-clear">
            Clear History
          </button>
        </div>
        <TokenHistoryTable history={tokenData.history} />
      </section>
    </div>
  );
}

// ============================================================================
// Bar Chart Component
// ============================================================================

function TokenBarChart({ history }) {
  if (!history || history.length === 0) return null;
  
  const maxTokens = Math.max(...history.map(h => h.total_tokens));
  const last20 = history.slice(-20);
  
  return (
    <div className="bar-chart-wrapper">
      <div className="chart-legend">
        <div className="legend-item">
          <span className="legend-color legend-prompt"></span>
          <span>Prompt (Input)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color legend-completion"></span>
          <span>Completion (Output)</span>
        </div>
      </div>
      
      <div className="bar-chart">
        {last20.map((entry, index) => {
          const promptHeight = (entry.prompt_tokens / maxTokens) * 100;
          const completionHeight = (entry.completion_tokens / maxTokens) * 100;
          const timestamp = new Date(entry.timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
          
          return (
            <div 
              key={index} 
              className="bar-container"
              title={`${timestamp}\nPrompt: ${entry.prompt_tokens.toLocaleString()}\nCompletion: ${entry.completion_tokens.toLocaleString()}\nTotal: ${entry.total_tokens.toLocaleString()}`}
            >
              <div className="bar-wrapper">
                {/* Completion tokens (orange) - shown on top */}
                <div 
                  className="bar bar-completion"
                  style={{
                    height: `${completionHeight}%`
                  }}
                />
                {/* Prompt tokens (blue) - shown below */}
                <div 
                  className="bar bar-prompt"
                  style={{
                    height: `${promptHeight}%`
                  }}
                />
              </div>
              <div className="bar-label" title={timestamp}>{timestamp}</div>
              <div className="bar-tokens">{entry.total_tokens.toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// History Table Component
// ============================================================================

function TokenHistoryTable({ history }) {
  const reversed = [...history].reverse();
  
  return (
    <div className="table-container">
      <table className="token-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Query</th>
            <th>
              Prompt
              <span className="column-hint" title="Input tokens: your question + system prompt + context">
                (Input)
              </span>
            </th>
            <th>
              Completion
              <span className="column-hint" title="Output tokens: the AI's generated response">
                (Output)
              </span>
            </th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {reversed.map((entry, index) => (
            <tr key={index}>
              <td className="time-cell">
                {new Date(entry.timestamp).toLocaleTimeString()}
              </td>
              <td className="query-cell" title={entry.query}>
                {entry.query}
              </td>
              <td className="number-cell">{entry.prompt_tokens.toLocaleString()}</td>
              <td className="number-cell">{entry.completion_tokens.toLocaleString()}</td>
              <td className="number-cell total-cell">{entry.total_tokens.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TokenDashboard;
